import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useHubMember } from "@/lib/HubMemberContext";
import { LogIn, Loader2, Mail, Lock, Sparkles } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

/**
 * Returning-member login page.
 *
 * Two equal-weight login paths shown side-by-side:
 *   1. Email + password  (fast for returning members who set one)
 *   2. Google / Gmail    (one-tap, also used on first ever sign-up)
 *
 * Both paths land the user at /me with the same session cookie.
 */
export default function MemberLogin() {
  const navigate = useNavigate();
  const { refresh } = useHubMember();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Enter your email and password");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/hub/login`,
        { email: email.trim(), password },
        { withCredentials: true },
      );
      toast.success(`Welcome back, ${data.user?.name || "member"}`);
      await refresh();
      navigate("/me", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="min-h-[calc(100vh-80px)] py-14 px-5" data-testid="member-login-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Member sign-in</div>
          <h1 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">Welcome Back.</h1>
          <p className="text-sm md:text-base text-white/60 mt-3 max-w-lg mx-auto">
            Sign in to manage memberships, book slots and track your plan hours. Pick whichever option works for you.
          </p>
        </div>

        {/* Two equal login paths */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Option 1 — Email + Password */}
          <div className="border border-white/12 bg-[#131A2B] p-6 md:p-7" data-testid="login-option-email">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-brand-orange text-black grid place-items-center font-black text-xs">1</div>
              <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Option 1</div>
            </div>
            <div className="font-display text-2xl font-black uppercase mt-2">Email + Password</div>
            <div className="text-xs text-white/50 mt-1 mb-5">Sign in with the email and password you set on your profile.</div>

            <form onSubmit={submit} className="space-y-3">
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Email</div>
                <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
                  <Mail size={14} className="ml-3 text-white/40" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="member-login-email" autoFocus autoComplete="email"
                    placeholder="you@example.com"
                    className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
                </div>
              </label>
              <label className="block">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Password</div>
                <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
                  <Lock size={14} className="ml-3 text-white/40" />
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="member-login-password" autoComplete="current-password"
                    placeholder="Your password"
                    className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
                </div>
              </label>
              <button type="submit" disabled={busy}
                data-testid="member-login-submit"
                className="w-full bg-brand-orange text-black py-3 font-bold text-sm uppercase tracking-widest hover:bg-white transition disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
                {busy ? "Signing in…" : "Sign in with password"}
              </button>
              <div className="text-center pt-1">
                <Link to="/member/forgot-password" data-testid="member-login-forgot"
                  className="text-[11px] font-mono uppercase tracking-widest brand-orange hover:text-white">
                  Forgot password?
                </Link>
              </div>
            </form>
          </div>

          {/* Option 2 — Google */}
          <div className="border border-brand-blue/40 bg-brand-blue/5 p-6 md:p-7" data-testid="login-option-google">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-full bg-brand-blue text-white grid place-items-center font-black text-xs">2</div>
              <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// Option 2</div>
            </div>
            <div className="font-display text-2xl font-black uppercase mt-2">Google / Gmail</div>
            <div className="text-xs text-white/50 mt-1 mb-5">One-tap sign-in with your Google account. No password needed.</div>

            <GoogleSignInButton returnTo="/me" label="Continue with Google" />

            <div className="mt-6 space-y-2 text-[11px] text-white/60">
              <div className="flex items-start gap-2">
                <Sparkles size={11} className="brand-orange mt-0.5 shrink-0" />
                <div>New here? Google sign-in creates your Hub Member ID automatically.</div>
              </div>
              <div className="flex items-start gap-2">
                <Sparkles size={11} className="brand-orange mt-0.5 shrink-0" />
                <div>Forgot your password? Just tap Google to sign in and reset it from your profile.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sign up path */}
        <div className="mt-10 text-center text-xs text-white/60">
          Don&apos;t have an account yet?{" "}
          <Link to="/member/signup" data-testid="login-signup-link" className="brand-orange font-bold hover:text-white">
            Create one in 30 seconds
          </Link>
        </div>

        {/* Parent link */}
        <div className="mt-3 text-center text-[11px] text-white/40">
          Coming from the old parent portal? Your login still works — same email + password.
        </div>
      </div>
    </section>
  );
}
